import React, {
  Fragment, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import Grid from '@material-ui/core/Grid';
import { RouteComponentProps } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/react-hooks';
import Typography from '@material-ui/core/Typography';
import CustomTablePagination from '../../components/CustomTablePagination';
import SelectedActionsBar from '../../components/SelectedActionsBar';
import ConfirmDelete from '../../components/ConfirmDelete';
import SearchBar from '../../components/SearchBar';
import { Status } from '../../types/Status';
import Spinner from '../../components/CustomSpinner';
// eslint-disable-next-line import/no-cycle
import PropertiesTable from './PropertiesTable/PropertiesTable';
import useStyles from './styles';
import { Query } from '../../utils/query';
import { Order } from '../../utils/table';
import { ROWS_PER_PAGE } from '../../constants/table';
import useDebounce from '../../hooks/useDebounce';
import CustomButton from '../../components/CustomButton';
import { ALL_PROPERTIES, REMOVE_PROPERTIES } from '../../graphql/queries/Properties';
import { initialState, SearchSortContext } from '../../context/SearchSortContext';
import RowsPerPageSelect from '../../components/RowsPerPageSelect/RowsPerPageSelect';
import downloadDocument from '../../utils/files';
import { loadExport, loadExportAll } from '../../services/export';
import { Lock, Property } from '../../types/Property';

type Props = {
  properties: Array<Property>;
  propertiesTotal: number;
  loading: boolean;
  loadProperties: (query: Query) => void;
  exportProperties: (selection: Array<number | string>) => void;
  printProperties: (selection: Array<number | string>) => void;
} & RouteComponentProps;

const searchBarPlaceholder: string = 'Search properties';

const Properties: React.FC<Props> = ({ history, match }) => {
  const classes = useStyles();
  const { dispatch, state } = useContext(SearchSortContext);
  const {
    orderBy, order, contextSearch, currentPage, rowsPerPageAmount,
  } = state.properties;
  const [search, setSearch] = useState<string>(contextSearch);
  const [cursor, setCursor] = useState<{limit: number, offset: number}>({
    limit: rowsPerPageAmount, offset: currentPage * rowsPerPageAmount,
  });
  const debouncedSearch = useDebounce(search, 1000);
  const [sort, setSort] = useState<{ sortBy: string, direction: 'asc' | 'desc' }>({ sortBy: orderBy, direction: order });
  const [rowsPerPage] = React.useState<number>(ROWS_PER_PAGE);
  const [propertyToRemove, setPropertyToRemove] = useState<boolean>(false);
  const [selected, setSelected] = useState<Array<Property['property_id']>>([]);
  const selectedLength = selected.length;

  const { loading, data } = useQuery<{ newProperties: Paginated<Property
  & {lock: Lock } & {disposition: Status}
  & {client: { name: string } }>}>(
    ALL_PROPERTIES, {
      variables: {
        limit: cursor.limit,
        offset: cursor.offset,
        search: debouncedSearch,
        sortBy: sort.sortBy,
        direction: sort.direction,
      },
      fetchPolicy: 'network-only',
    },
  );

  const [exportAllLoading, setExportAllLoading] = useState<boolean>(false);

  const [removeProperties] = useMutation(REMOVE_PROPERTIES, {
    refetchQueries: [{
      query: ALL_PROPERTIES,
      variables: {
        limit: cursor.limit,
        offset: cursor.offset,
        search: debouncedSearch,
        sortBy: sort.sortBy,
        direction: sort.direction,
      },
    }],
  });

  const page = useMemo(
    () => Math.ceil(cursor.offset / cursor.limit),
    [cursor.limit, cursor.offset],
  );

  useEffect(() => () => {
    const pageNameIndex = 1;
    const pathElements = history.location.pathname.split('/');
    const pathname = pathElements[pageNameIndex];
    if (pathElements.length === 3) {
      return;
    }
    if ((pathname !== 'properties')) {
      dispatch({
        type: 'RESET_STATE',
      });
    }
  }, [dispatch, history.location.pathname]);

  const handleSearch = useCallback((value:string) => {
    dispatch({
      type: 'CHANGE_PROPERTY',
      payload: { contextSearch: value, currentPage: 0 },
    });
    setSearch(value);
    setCursor({ limit: rowsPerPageAmount, offset: 0 });
  }, [dispatch, rowsPerPageAmount]);


  const handleShowAllClick = () => {
    dispatch({ type: 'RESET_STATE' });
    setSort({
      direction: 'desc',
      sortBy: 'property.created_on',
    });
    setSearch('');
  };

  const properties = data ? data.newProperties.list : [];
  const params = data ? data.newProperties.params : { total: 0 };

  useEffect(() => {
    setSelected([]);
  }, [setSelected, page, rowsPerPage]);

  function handleChangeOrder(newOrder: Order, newOrderBy: string) {
    if (newOrderBy !== 'agents.agent_username') {
      setSort({
        direction: newOrder,
        sortBy: newOrderBy,
      });
      setCursor({ offset: 0, limit: rowsPerPageAmount });
      dispatch({
        type: 'CHANGE_PROPERTY',
        payload: {
          orderBy: newOrderBy,
          order: newOrder,
          currentPage: 0,
        },
      });
    }
  }


  const handleChangePage = useCallback((_e: unknown, nPage: number) => {
    setCursor(prevCursor => ({ ...prevCursor, offset: prevCursor.limit * nPage }));
    dispatch({
      type: 'CHANGE_PROPERTY',
      payload: {
        currentPage: nPage,
      },
    });
  }, [dispatch]);

  const handleChangeRowsPerPage = useCallback(e => {
    setCursor({ offset: 0, limit: e.target.value });
    dispatch({
      type: 'CHANGE_PROPERTY',
      payload: {
        currentPage: 0,
        rowsPerPageAmount: e.target.value,
      },
    });
  }, [dispatch]);

  const handleDelete = () => {
    if (selectedLength !== 0) {
      removeProperties({
        variables: {
          properties: selected,
        },
      }).then(() => setSelected([]));
    }
  };

  function handleSelectAllClick(event: React.ChangeEvent<HTMLInputElement>) {
    if (event.target.checked) {
      const newSelecteds = properties.map(i => i.property_id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  }

  function handlePropertySelect(propertyId: number) {
    history.push(`${match.url}/${propertyId}`);
  }

  function handleClick(
    event: React.SyntheticEvent,
    id: Property['property_id'],
  ) {
    event.preventDefault();

    const selectedIndex = selected.indexOf(id);
    let newSelected: Array<Property['property_id']> = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }

    setSelected(newSelected);
  }

  function handleExport() {
    if (selectedLength !== 0) {
      loadExport(selected, 'properties')
        .then(response => {
          const contentType = response.headers['Content-Type'];
          downloadDocument(contentType, 'Properties', response.data);
        });
    }
  }

  function handleExportAll() {
    setExportAllLoading(true);
    loadExportAll('properties').then(response => {
      setExportAllLoading(false);
      const contentType = response.headers['Content-Type'];
      downloadDocument(contentType, 'Properties', response.data);
    });
  }

  function handlePrint() {
  }

  const isStateInitial = JSON.stringify({
    contextSearch: state.properties.contextSearch,
    order: state.properties.order,
    orderBy: state.properties.orderBy,
  }) === JSON.stringify({
    contextSearch: initialState.properties.contextSearch,
    order: initialState.properties.order,
    orderBy: initialState.properties.orderBy,
  });

  return (
    <Fragment>
      <Grid container spacing={5}>
        <Grid item className={classes.controlButton}>
          <CustomButton
            variant="orange"
            color="primary"
            onClick={() => history.push(`${match.path}/add`)}
          >
            Add property
          </CustomButton>
        </Grid>
        <Grid item className={classes.controlButton}>
          <CustomButton
            variant="orange"
            color="primary"
            onClick={() => history.push('/generator')}
          >
            Install generator
          </CustomButton>
        </Grid>
        { !isStateInitial && (
          <Grid item className={classes.controlButton}>
            <CustomButton
              variant="orange"
              color="primary"
              onClick={handleShowAllClick}
            >
              Show all
            </CustomButton>
          </Grid>
        )}
      </Grid>
      <Grid container>
        <Grid item xs>
          <SearchBar
            placeholder={searchBarPlaceholder}
            defaultSearchValue={search}
            onChange={handleSearch}
          />
        </Grid>
      </Grid>
      <div className={classes.contentWrapper}>
        {loading && (
          <div className={classes.spinnerWrapper}>
            <Spinner />
          </div>
        )}
        <SelectedActionsBar
          onDelete={() => { setPropertyToRemove(true); }}
          onExport={handleExport}
          onExportAll={handleExportAll}
          onPrint={handlePrint}
          selectedLength={selectedLength}
          exportAllLoading={exportAllLoading}
        />
        <Typography className={classes.tableHeaderText} variant="h5">Properties</Typography>
        <div className={classes.tableWrapper}>
          <Grid container alignContent="center" justify="flex-end">
            <RowsPerPageSelect
              rowsPerPageOptions={[25, 50, 100, 150]}
              rowsPerPage={cursor.limit}
              onChangeRowsPerPage={n => handleChangeRowsPerPage({ target: { value: n } })}
            />
          </Grid>
          <PropertiesTable
            selected={selected}
            properties={properties}
            order={sort.direction}
            orderBy={sort.sortBy}
            rowsPerPage={rowsPerPage}
            onChangeOrder={handleChangeOrder}
            onSelect={handleClick}
            onSelectAll={handleSelectAllClick}
            onRowClick={handlePropertySelect}
          />
          <CustomTablePagination
            rowsPerPageOptions={[25, 50, 100, 150]}
            rowsTotal={params.total}
            rowsCount={properties.length}
            rowsPerPage={cursor.limit}
            currentPage={page}
            onChangePage={n => handleChangePage(undefined, n)}
            onChangeRowsPerPage={n => handleChangeRowsPerPage({ target: { value: n } })}
          />
        </div>
      </div>
      <ConfirmDelete
        isOpen={propertyToRemove}
        onCancel={() => { setPropertyToRemove(false); }}
        onConfirm={() => { handleDelete(); setPropertyToRemove(false); }}
      />
    </Fragment>
  );
};

export default React.memo(Properties);
